import React, { useEffect, useRef, useState } from "react";
import { api, API, formatError } from "../lib/api";
import { Send, Volume2, Loader2, RotateCcw, Sparkles, Bot, User as UserIcon, Square } from "lucide-react";
import { toast } from "sonner";
import VoiceMode from "./VoiceMode";

const SUGGESTIONS = [
  "What's in Module 3?",
  "How do I price my product for retail?",
  "What margin should a broker expect?",
  "Draft a cold pitch to a Whole Foods buyer",
];

/**
 * Shared chat UI used by both the /assistant page (variant="page")
 * and the floating help bubble (variant="bubble").
 */
export default function AssistantChat({ variant = "page", onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [playing, setPlaying] = useState(null); // index of message currently playing
  const scrollRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    try {
      const { data } = await api.post("/assistant/chat", {
        message: text,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages([...nextMessages, {
        role: "assistant",
        content: data.reply,
        provider: data.provider,
        model: data.model,
      }]);
    } catch (e) {
      setMessages([...nextMessages, {
        role: "assistant",
        content: "Sorry — I couldn't reach the assistant. Please try again.",
        error: true,
      }]);
      toast.error(formatError(e));
    } finally {
      setSending(false);
    }
  };

  const startNewSession = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
  };

  const playTts = async (idx, text) => {
    // Stop anything currently playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playing === idx) { setPlaying(null); return; }
    setPlaying(idx);
    try {
      const res = await fetch(`${API}/assistant/tts`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("TTS request failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { setPlaying(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPlaying(null); URL.revokeObjectURL(url); };
      audioRef.current = audio;
      await audio.play();
    } catch (err) {
      setPlaying(null);
      toast.error(err.message || "Could not play audio");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const isBubble = variant === "bubble";
  const shellClass = isBubble
    ? "flex flex-col h-full bg-white rounded-2xl overflow-hidden"
    : "flex flex-col rounded-2xl border border-shell-border bg-white shadow-sm overflow-hidden";
  const scrollClass = isBubble
    ? "flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30"
    : "h-[520px] overflow-y-auto p-6 space-y-5 bg-muted/30";

  return (
    <div className={shellClass} data-testid={`assistant-chat-${variant}`}>
      {isBubble && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-shell-border bg-shell-side text-white">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-sky-400 grid place-items-center">
              <Sparkles className="w-3.5 h-3.5" />
            </span>
            <span className="font-semibold text-sm">Morgan</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={startNewSession} className="p-1.5 rounded hover:bg-white/10" title="New chat"
              data-testid="assistant-new">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10 text-lg leading-none"
              data-testid="assistant-close">×</button>
          </div>
        </div>
      )}

      <div ref={scrollRef} className={scrollClass} data-testid="assistant-scroll">
        {messages.length === 0 && (
          <div className="text-center py-10" data-testid="assistant-empty">
            <div className="w-14 h-14 rounded-2xl mx-auto grid place-items-center bg-gradient-to-br from-primary to-sky-400 shadow-lg">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <p className="mt-4 font-display text-2xl">Ask anything CPG.</p>
            <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
              I know every module inside CPG Institute and can pull in Claude or ChatGPT for open questions.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid={`assistant-suggest-${s.slice(0, 12)}`}
                  className="text-xs border border-shell-border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <MessageBubble key={i} msg={m} idx={i}
            onPlay={() => playTts(i, m.content)} playing={playing === i} />
        ))}

        {sending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="assistant-typing">
            <Loader2 className="w-4 h-4 animate-spin" /> Morgan is thinking…
          </div>
        )}
      </div>

      <div className="border-t border-shell-border p-3 bg-white">
        <div className="flex items-end gap-2 min-w-0">
          {isBubble && <VoiceMode compact />}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            data-testid="assistant-input"
            rows={1}
            placeholder="Ask about a module, pricing, buyers, or anything CPG…"
            className="flex-1 min-w-0 resize-none rounded-2xl border border-shell-border px-4 py-2.5 text-sm focus:outline-none focus:border-primary max-h-32"
          />
          <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="assistant-send"
            className="w-10 h-10 shrink-0 rounded-full bg-primary text-white grid place-items-center hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        {!isBubble && messages.length > 0 && (
          <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Auto-routes: course questions → Claude · open questions → GPT</span>
            <button onClick={startNewSession} data-testid="assistant-reset"
              className="inline-flex items-center gap-1 hover:text-primary">
              <RotateCcw className="w-3 h-3" /> New chat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ msg, idx, onPlay, playing }) {
  const isUser = msg.role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end" data-testid={`assistant-msg-${idx}`}>
        <div className="max-w-[80%] bg-primary text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm whitespace-pre-wrap shadow-sm">
          {msg.content}
        </div>
      </div>
    );
  }
  const providerLabel = msg.provider === "anthropic" ? "Claude" : msg.provider === "openai" ? "ChatGPT" : null;
  return (
    <div className="flex items-start gap-3" data-testid={`assistant-msg-${idx}`}>
      <span className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-sky-400 grid place-items-center text-white">
        <Bot className="w-4 h-4" />
      </span>
      <div className="max-w-[80%]">
        <div className={["rounded-2xl rounded-tl-md px-4 py-3 text-sm whitespace-pre-wrap shadow-sm",
          msg.error ? "bg-destructive/10 text-destructive" : "bg-white border border-shell-border"].join(" ")}>
          {msg.content}
        </div>
        <div className="mt-1.5 flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
          {providerLabel && <span data-testid={`assistant-provider-${idx}`}>{providerLabel}</span>}
          {!msg.error && (
            <button onClick={onPlay} data-testid={`assistant-tts-${idx}`}
              className="inline-flex items-center gap-1 hover:text-primary transition-colors">
              {playing ? (<><Square className="w-3 h-3" /> Stop</>) : (<><Volume2 className="w-3 h-3" /> Read aloud</>)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
